/**
 * Regras puras de inventário (doc 03, Operações de pilha). O servidor aplica as intenções
 * atômicas sobre o estado; o cliente só exibe. Não existe "item no cursor": arrastar é só UI e
 * a operação acontece inteira ao soltar, o que elimina perda e duplicação de itens.
 * Um slot vazio tem `itemId === ""`.
 */
export interface StackLike {
  itemId: string;
  qty: number;
}

export type StackSizeOf = (itemId: string) => number;

/** Inventário: cinto 8 + mochila 16; 4 slots de armadura; 1 Aljava (doc 07, I1). */
export const INVENTORY_SLOTS = 24;
export const BELT_SLOTS = 8;
export const ARMOR_SLOT_COUNT = 4;

export type SlotArea = "inv" | "armor" | "quiver" | "estacao";
export interface SlotRef {
  area: SlotArea;
  index: number;
}

/** Estação aberta pelo jogador (Forno, Panela): os slots dela e o que cada um aceita. */
export interface StationSlots {
  slots: StackLike[];
  accepts(index: number, itemId: string): boolean;
  /** Para onde vai, no Shift+clique, um item vindo do inventário (ou `undefined`). */
  quickTarget(itemId: string): number | undefined;
}

export interface InventoryLike {
  slots: StackLike[];
  armor: StackLike[];
  quiver: StackLike;
  selected: number;
  /** Estação aberta: os slots dela viram a área `estacao` nas mesmas operações atômicas. */
  station?: StationSlots | undefined;
}

/** O que as regras precisam saber de cada item (vem do catálogo de libs/content). */
export interface ItemRules {
  stackOf(itemId: string): number;
  /** Índice do slot de armadura (0 cabeça, 1 tronco, 2 pernas, 3 pés) ou `undefined`. */
  armorSlotOf(itemId: string): number | undefined;
  isAmmo(itemId: string): boolean;
}

export type OpResult = { ok: true } | { ok: false; reason: string };
const OK: OpResult = { ok: true };
const fail = (reason: string): OpResult => ({ ok: false, reason });

const isEmpty = (s: StackLike): boolean => s.itemId === "" || s.qty <= 0;

function clear(s: StackLike): void {
  s.itemId = "";
  s.qty = 0;
}

export function getSlot(inv: InventoryLike, ref: SlotRef): StackLike | undefined {
  if (!Number.isInteger(ref.index) || ref.index < 0) return undefined;
  if (ref.area === "inv") return inv.slots[ref.index];
  if (ref.area === "armor") return inv.armor[ref.index];
  if (ref.area === "estacao") return inv.station?.slots[ref.index];
  return ref.index === 0 ? inv.quiver : undefined;
}

/**
 * O item pode ocupar este slot? Mochila aceita tudo; armadura só no slot certo; Aljava só munição;
 * a estação decide pelos slots dela (combustível, entradas; a saída não recebe).
 */
export function canPlace(rules: ItemRules, ref: SlotRef, itemId: string, inv?: InventoryLike): boolean {
  if (itemId === "") return true;
  if (ref.area === "inv") return true;
  if (ref.area === "armor") return rules.armorSlotOf(itemId) === ref.index;
  if (ref.area === "estacao") return inv?.station?.accepts(ref.index, itemId) === true;
  return rules.isAmmo(itemId);
}

/**
 * Adiciona `qty` de `itemId` aos slots: completa pilhas existentes, depois ocupa slots vazios em
 * ordem. Altera `slots` e devolve a sobra que não coube.
 */
export function addItem(slots: StackLike[], itemId: string, qty: number, stackOf: StackSizeOf): number {
  const max = Math.max(1, stackOf(itemId));
  let left = qty;
  for (const slot of slots) {
    if (left <= 0) break;
    if (slot.itemId !== itemId || slot.qty >= max) continue;
    const moved = Math.min(left, max - slot.qty);
    slot.qty += moved;
    left -= moved;
  }
  for (const slot of slots) {
    if (left <= 0) break;
    if (!isEmpty(slot)) continue;
    const moved = Math.min(left, max);
    slot.itemId = itemId;
    slot.qty = moved;
    left -= moved;
  }
  return left;
}

/** Coleta para o inventário: munição vai primeiro para a Aljava; o resto, para os slots. */
export function collectItem(inv: InventoryLike, itemId: string, qty: number, rules: ItemRules): number {
  let left = qty;
  if (rules.isAmmo(itemId)) left = addItem([inv.quiver], itemId, left, rules.stackOf);
  return left > 0 ? addItem(inv.slots, itemId, left, rules.stackOf) : 0;
}

/** Quanto de `itemId` cabe nos slots. */
export function spaceFor(slots: readonly StackLike[], itemId: string, stackOf: StackSizeOf): number {
  const max = Math.max(1, stackOf(itemId));
  let space = 0;
  for (const slot of slots) {
    if (slot.itemId === itemId) space += Math.max(0, max - slot.qty);
    else if (isEmpty(slot)) space += max;
  }
  return space;
}

export function countItem(slots: readonly StackLike[], itemId: string): number {
  let n = 0;
  for (const slot of slots) if (slot.itemId === itemId) n += slot.qty;
  return n;
}

/**
 * Move `qty` (padrão: a pilha toda) de `from` para `to`. Destino vazio recebe; mesmo item junta
 * até o máximo da pilha; item diferente troca de lugar (só pilha inteira e se ambos couberem).
 */
export function moveStack(inv: InventoryLike, from: SlotRef, to: SlotRef, rules: ItemRules, qty?: number): OpResult {
  const src = getSlot(inv, from);
  const dst = getSlot(inv, to);
  if (!src || !dst) return fail("slot inválido");
  if (src === dst) return fail("mesmo slot");
  if (isEmpty(src)) return fail("origem vazia");
  const amount = Math.min(src.qty, Math.max(1, Math.floor(qty ?? src.qty)));
  if (!canPlace(rules, to, src.itemId, inv)) return fail("item não cabe nesse slot");
  if (isEmpty(dst)) {
    dst.itemId = src.itemId;
    dst.qty = amount;
    src.qty -= amount;
    if (src.qty <= 0) clear(src);
    return OK;
  }
  if (dst.itemId === src.itemId) {
    const moved = Math.min(amount, rules.stackOf(src.itemId) - dst.qty);
    if (moved <= 0) return fail("pilha cheia");
    dst.qty += moved;
    src.qty -= moved;
    if (src.qty <= 0) clear(src);
    return OK;
  }
  if (amount !== src.qty) return fail("só dá para trocar a pilha inteira");
  if (!canPlace(rules, from, dst.itemId, inv)) return fail("a troca não cabe na origem");
  const { itemId, qty: dstQty } = dst;
  dst.itemId = src.itemId;
  dst.qty = src.qty;
  src.itemId = itemId;
  src.qty = dstQty;
  return OK;
}

/** Divide a pilha: a metade menor vai para o primeiro slot livre da mochila/cinto. */
export function splitStack(inv: InventoryLike, ref: SlotRef): OpResult {
  const src = getSlot(inv, ref);
  if (!src || isEmpty(src)) return fail("origem vazia");
  if (src.qty < 2) return fail("pilha de 1 não divide");
  const free = inv.slots.find(isEmpty);
  if (!free) return fail("sem slot livre");
  const half = Math.floor(src.qty / 2);
  free.itemId = src.itemId;
  free.qty = half;
  src.qty -= half;
  return OK;
}

/**
 * Transferência rápida (Shift+clique): armadura e munição vão para seus slots; o que está neles
 * volta para a mochila; no inventário, cinto ↔ mochila.
 */
export function quickTransfer(inv: InventoryLike, ref: SlotRef, rules: ItemRules): OpResult {
  const src = getSlot(inv, ref);
  if (!src || isEmpty(src)) return fail("origem vazia");
  // Com uma estação aberta, o Shift+clique no inventário manda para ela (combustível no slot de
  // fogo, o resto nas entradas).
  if (ref.area === "inv" && inv.station) {
    const target = inv.station.quickTarget(src.itemId);
    if (target === undefined) return fail("a estação não aceita esse item");
    return moveStack(inv, ref, { area: "estacao", index: target }, rules);
  }
  if (ref.area !== "inv") {
    const left = addItem(inv.slots, src.itemId, src.qty, rules.stackOf);
    if (left === src.qty) return fail("inventário cheio");
    src.qty = left;
    if (left <= 0) clear(src);
    return OK;
  }
  const armorIndex = rules.armorSlotOf(src.itemId);
  if (armorIndex !== undefined) return moveStack(inv, ref, { area: "armor", index: armorIndex }, rules);
  if (rules.isAmmo(src.itemId)) return moveStack(inv, ref, { area: "quiver", index: 0 }, rules);
  const target = ref.index < BELT_SLOTS ? inv.slots.slice(BELT_SLOTS) : inv.slots.slice(0, BELT_SLOTS);
  const left = addItem(target, src.itemId, src.qty, rules.stackOf);
  if (left === src.qty) return fail("sem espaço do outro lado");
  src.qty = left;
  if (left <= 0) clear(src);
  return OK;
}

/** Junta todas as pilhas de `itemId` (duplo clique), enchendo as primeiras. */
export function gatherStacks(inv: InventoryLike, itemId: string, rules: ItemRules): OpResult {
  const stacks = inv.slots.filter((s) => s.itemId === itemId && s.qty > 0);
  if (stacks.length < 2) return fail("nada a juntar");
  const total = stacks.reduce((n, s) => n + s.qty, 0);
  const max = Math.max(1, rules.stackOf(itemId));
  let left = total;
  for (const s of stacks) {
    const take = Math.min(left, max);
    s.qty = take;
    left -= take;
    if (take <= 0) clear(s);
  }
  return OK;
}

/** Retira `qty` (padrão: tudo) de um slot e devolve o que saiu (para descartar no chão). */
export function takeFromSlot(inv: InventoryLike, ref: SlotRef, qty?: number): StackLike | undefined {
  const src = getSlot(inv, ref);
  if (!src || isEmpty(src)) return undefined;
  const amount = Math.min(src.qty, Math.max(1, Math.floor(qty ?? src.qty)));
  const out = { itemId: src.itemId, qty: amount };
  src.qty -= amount;
  if (src.qty <= 0) clear(src);
  return out;
}

/** Remove `qty` de `itemId` dos slots (fabricação, consumo). Devolve se havia o suficiente. */
export function consumeItem(slots: StackLike[], itemId: string, qty: number): boolean {
  if (countItem(slots, itemId) < qty) return false;
  let left = qty;
  for (let i = slots.length - 1; i >= 0 && left > 0; i--) {
    const s = slots[i] as StackLike;
    if (s.itemId !== itemId) continue;
    const take = Math.min(left, s.qty);
    s.qty -= take;
    left -= take;
    if (s.qty <= 0) clear(s);
  }
  return true;
}

export function selectBelt(inv: InventoryLike, index: number): OpResult {
  if (!Number.isInteger(index) || index < 0 || index >= BELT_SLOTS) return fail("slot do cinto inválido");
  inv.selected = index;
  return OK;
}

/** Item na mão: o do slot selecionado do cinto. */
export function equippedItem(inv: Pick<InventoryLike, "slots" | "selected">): string {
  return inv.slots[inv.selected]?.itemId ?? "";
}
